import React, { useMemo } from 'react'
import { formatDate } from '~/utils/date'
import { mutualTheme } from '~/utils/theme'
import { makeStyles } from '@material-ui/styles'
import * as illustrations from '~/assets/illustrations'
import { Box, Grid, Hidden, Typography } from '@material-ui/core'
import { AccountAnalysisStatus } from '@mutual-services/account-types'
import { StatusIconContainer, TimeContainer, BadgeContainer } from './components'
import { getAnalysisStatusIllustration, translateAnalysisStatus } from '~/utils/analysisStatus'

type BadgeType = 'error' | 'success' | 'info' | 'warning'

export type StatusBadgeProps = {
  type: BadgeType,
  termTime: number,
  analysisDate: Date | null,
  status: AccountAnalysisStatus,
  solicitationDate: Date | string
}

const useStyles = makeStyles({
  container: {
    width: '100%',
    flexDirection: 'row',
    boxShadow: mutualTheme.shadows[2],
    borderRadius: mutualTheme.shape.borderRadius
  },
  title: (props: StatusBadgeProps) => ({
    fontWeight: 700,
    color: mutualTheme.palette.badgeBorderColor[props.type].main
  }),
  firstHalf: (props: StatusBadgeProps) => ({
    width: '65%',
    padding: mutualTheme.spacing(4),
    borderTopLeftRadius: mutualTheme.shape.borderRadius,
    borderBottomLeftRadius: mutualTheme.shape.borderRadius,
    backgroundColor: mutualTheme.palette.badgeBackgroundColor[props.type].dark,
    [mutualTheme.breakpoints.down('md')]: {
      width: '100%',
      padding: mutualTheme.spacing(3),
      borderTopRightRadius: mutualTheme.shape.borderRadius,
      borderBottomRightRadius: mutualTheme.shape.borderRadius
    }
  }),
  secondHalf: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  itemContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: mutualTheme.spacing(4),
    color: mutualTheme.palette.common.white,
    [mutualTheme.breakpoints.down('md')]: {
      alignItems: 'flex-start'
    }
  }
})

export const StatusBadge: React.FC<StatusBadgeProps> = (props) => {
  const { solicitationDate, status, analysisDate, type } = props
  const classes = useStyles(props)

  const illustration = useMemo(() => getAnalysisStatusIllustration(status), [status])
  const Illustration = useMemo(() => illustrations[illustration], [illustration])

  const date = useMemo(() => (
    type === 'info'
      ? analysisDate ? `${formatDate(analysisDate)} às ${new Date(analysisDate).getHours()}:${new Date(analysisDate).getMinutes()}h` : '-'
      : formatDate(solicitationDate)
  ), [solicitationDate])

  return (
    <Grid className={classes.container} container>
      <Box className={classes.firstHalf}>
        <Typography variant='airbnbH4' className={classes.title}>Seu pedido de empréstimo</Typography>
        <BadgeContainer
          type={type}
          icon='CalendarIcon'
          description={date}
          title={type === 'info' ? 'Documentação enviada em' : 'Solicitação iniciada em:'}
        />
        <BadgeContainer
          type={type}
          title='Status:'
          description={translateAnalysisStatus(status)}
          iconComponent={<StatusIconContainer status={status} type={type} />}
        />
        <Box className={classes.itemContainer}>
          <TimeContainer status={status} analysisDate={analysisDate} type={type} />
        </Box>
      </Box>
      <Hidden mdDown>
        <Box className={classes.secondHalf}>
          <Illustration />
        </Box>
      </Hidden>
    </Grid>
  )
}
